import { createWebHistory, createRouter } from "vue-router";
import axios from 'axios';
import routes from './routes'
import appConfig from "../../app.config";

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { top: 0, left: 0 }
  },
})

// CACHE untuk token validation
let authCache = {
  token: null,
  valid: false,
  timestamp: null,
  ttl: 5 * 60 * 1000 // 5 menit
};

// Fungsi helper untuk validasi token dengan caching
const validateToken = async (forceRefresh = false) => {
  const startTime = performance.now();
  
  const jwt = localStorage.getItem('jwt');
  if (!jwt) {
    authCache = { token: null, valid: false, timestamp: null, ttl: 5 * 60 * 1000 };
    return false;
  }

  const token = `Bearer ${process.env.VUE_APP_CLIENT_ID}.${jwt}`;
  
  // CHECK CACHE - hanya call API jika perlu
  if (!forceRefresh && authCache.token === token && authCache.timestamp) {
    const age = Date.now() - authCache.timestamp;
    
    if (age < authCache.ttl) {
      const cacheHitTime = (performance.now() - startTime).toFixed(2);
      console.log(`Auth Cache Hit (age: ${Math.round(age/1000)}s) - ${cacheHitTime}ms`);
      return authCache.valid;
    } else {
      console.log(`Cache expired (age: ${Math.round(age/1000)}s) - refreshing...`);
    }
  }
  
  try {
    const apiStartTime = performance.now();
    
    const { data } = await axios.get(
      `${process.env.VUE_APP_URL_API}member-api/authorization`,
      { 
        params: { token },
        timeout: 10000 // 10 detik timeout
      }
    );
    
    const apiDuration = (performance.now() - apiStartTime).toFixed(2);
    
    const saveStartTime = performance.now();
    localStorage.setItem('userdata', JSON.stringify(data.Data));
    localStorage.setItem('userid', data.Data.ID);
    const saveDuration = (performance.now() - saveStartTime).toFixed(2);
    
    // UPDATE CACHE
    authCache = {
      token: token,
      valid: true,
      timestamp: Date.now(),
      ttl: 5 * 60 * 1000
    };
    
    const totalTime = (performance.now() - startTime).toFixed(2);
    
    console.log(`Auth API Call - Backend: ${apiDuration}ms | Local: ${saveDuration}ms | Total: ${totalTime}ms`);
    
    return true;
  } catch (error) {
    const errorTime = (performance.now() - startTime).toFixed(2);
    console.error(`Auth failed after ${errorTime}ms:`, error.message);
    
    // 🛡️ TOLERANSI NETWORK / TRANSIENT ERROR:
    // Jika backend lambat/timeout atau network error, TAPI data user & jwt masih lengkap di localStorage,
    // jangan langsung paksa logout! Berikan grace period agar user tidak ter-kick ke login page.
    const isNetworkOrTimeout = !error.response || error.code === 'ECONNABORTED' || (error.message && error.message.includes('Network Error'));
    const hasLocalAuth = localStorage.getItem('jwt') && localStorage.getItem('user');

    if (isNetworkOrTimeout && hasLocalAuth) {
      console.warn('⚠️ Backend temporarily unreachable, using existing local session grace period');
      authCache = {
        token: token,
        valid: true,
        timestamp: Date.now(),
        ttl: 1 * 60 * 1000 // Grace period 1 menit
      };
      return true;
    }

    // Jika server merespons 401/403 secara eksplisit, barulah invalidate session
    authCache = { token: null, valid: false, timestamp: null, ttl: 5 * 60 * 1000 };
    return false;
  }
}

// Before each route evaluates...
router.beforeEach(async (routeTo, routeFrom, next) => {
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);

  if (!authRequired) {
    return next();
  }

  const isValid = await validateToken();
  
  if (isValid) {
    next();
  } else {
    next({ 
      name: 'login', 
      query: { redirectFrom: routeTo.fullPath } 
    });
  }
})

router.beforeResolve(async (routeTo, routeFrom, next) => {
  try {
    for (const route of routeTo.matched) {
      if (route.meta?.beforeResolve) {
        await new Promise((resolve, reject) => {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            if (args.length) {
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        });
      }
    }
  } catch (error) {
    return;
  }

  document.title = `${routeTo.meta.title} | ${appConfig.title}`;
  
  next();
})

// Export fungsi untuk force refresh (misal setelah login)
export function refreshAuth() {
  authCache = { token: null, valid: false, timestamp: null, ttl: 5 * 60 * 1000 };
}

export default router;
